import Lottie from "lottie-react";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import animationData from "../../../assets/json/spin-the-wheel.json";
import spinTheWheelEn from "../../../assets/spin_the_wheel_en.svg";
import { USER_TYPE } from "../../../constants/common";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";
import CustomModal from "../../CustomModal";

const SpinWheelModal = forwardRef(({}, ref) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [lessonID, setLessonID] = useState(0);
  const navigate = useNavigateWithLang();
  const lang = getLanguage();
  const lottieRef = useRef();
  const [difficulty, setDifficulty] = useState(null);
  const [studentChallengeStats, setStudentChallengeStats] = useState(null);

  useImperativeHandle(ref, () => ({
    openModal(id, payload) {
      setLessonID(id);
      setStudentChallengeStats(payload);
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const randomProperty = (obj) => {
    const keys = Object.keys(obj);
    return obj[keys[Math.floor(Math.random() * keys.length)]];
  };

  const handleSelect = (diff) => {
    setDifficulty(diff);
    const easySegments = { arr1: [600, 700], arr2: [700, 800], arr3: [800, 900] };
    const hardSegments = { arr1: [300, 400], arr2: [400, 500], arr3: [500, 600] };

    const segments = diff === 1 ? randomProperty(easySegments) : randomProperty(hardSegments);

    if (lottieRef.current) {
      lottieRef.current.setSpeed(1);
      lottieRef.current.playSegments(segments, true);
    }
  };

  const handleComplete = () => {
    if (difficulty != null) {
      navigate(`/${USER_TYPE.STUDENT.ROLE}/coding-area/challenge/${lessonID}/${difficulty}`);
      setIsOpen(false);
      setDifficulty(null); // reset
    }
  };

  return (
    <CustomModal
      isOpen={isOpen}
      // withCloseButton={false}
      onClose={() => setIsOpen(false)}
      className="max-w-[50rem] rounded-3xl border-8 border-blue-500 bg-[#F1F1F1] shadow-xl"
      contentClassname="p-6 bg-[#F1F1F1] px-0"
    >
      <div className="flex flex-col items-center p-4 text-center">
        {/* Stats */}
        <div className="mb-6 flex gap-10 text-sm">
          {/* Approved */}
          <div className="flex items-center gap-1 text-green-500">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-500 text-xs text-white">
              {studentChallengeStats?.total_approved}
            </span>
            <p>{t("approved")}</p>
          </div>

          {/* Pending */}
          <div className="flex items-center gap-1 text-yellow-400">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-yellow-400 text-xs text-white">
              {studentChallengeStats?.total_pending}
            </span>
            <p>{t("pending")}</p>
          </div>

          {/* Disapproved */}
          <div className="flex items-center gap-1 text-red-500">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white">
              {studentChallengeStats?.total_rejected}
            </span>
            <p>{t("disapproved")}</p>
          </div>
        </div>
        {lang == "ar" ? (
          <h2 className="my-4 text-4xl font-bold text-blue-500">دوّر العجلة!</h2>
        ) : (
          <img src={spinTheWheelEn} className="w-72" alt="spin" />
        )}
        <div className="ml-[-85px]">
          <Lottie
            lottieRef={lottieRef}
            onComplete={handleComplete}
            animationData={animationData}
            autoplay={false}
            loop={false}
          />
        </div>
        <div className="mt-8 flex w-full items-center justify-center gap-12">
          <button
            onClick={() => handleSelect(1)}
            disabled={studentChallengeStats?.student_easy_solved === studentChallengeStats?.total_easy}
            className={`wavy-btn relative w-2/6 rounded-3xl border-4 border-[#e0ffc6] p-4 text-lg font-semibold text-white transition duration-200 ${
              studentChallengeStats?.student_easy_solved === studentChallengeStats?.total_easy
                ? "cursor-not-allowed bg-gray-400"
                : "bg-[#2FA84F] hover:bg-green-500"
            }`}
          >
            {t("easy")} {studentChallengeStats?.student_easy_solved}/{studentChallengeStats?.total_easy}
          </button>

          <button
            onClick={() => handleSelect(2)}
            disabled={studentChallengeStats?.student_hard_solved === studentChallengeStats?.total_hard}
            className={`wavy-btn relative w-2/6 rounded-3xl border-4 border-[#ffe4af] p-4 text-lg font-semibold text-white transition duration-200 ${
              studentChallengeStats?.student_hard_solved === studentChallengeStats?.total_hard
                ? "cursor-not-allowed bg-gray-400"
                : "bg-orange-500 hover:bg-orange-300"
            }`}
          >
            {t("hard")} {studentChallengeStats?.student_hard_solved}/{studentChallengeStats?.total_hard}
          </button>
        </div>
      </div>
    </CustomModal>
  );
});

export default SpinWheelModal;
